import { useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import { useDataApi } from '@adsk/alloy-helpers';
import { useGetAccountCompanies } from '@common/hooks';
import { buildProjectCompaniesRequest } from '@common/utils';
import { COMPONENT_VARIANTS, DEBOUNCE_TIME_MS } from '@common/constants';

const useGetCompanies = (enableAddCompanyDebounce, variant) => {
  const [debouncedCompanyInput, setDebouncedCompanyInput] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const projectId = useCurrentProjectId();
  const {
    accountCompanies,
    onChangeCompanyInput: setAccountCompaniesDebounceInput,
    isFetchingAccountCompanies,
  } = useGetAccountCompanies(true);

  const {
    data: projectCompaniesData,
    loading: isFetchingProjectCompanies,
    doFetch: doGetProjectCompanies,
  } = useDataApi();

  const handleCompanyInputChange = (companyInput) => {
    setDebouncedCompanyInput(companyInput);
    setAccountCompaniesDebounceInput(companyInput);
  };

  const debouncedInputChange = useRef(
    debounce(handleCompanyInputChange, DEBOUNCE_TIME_MS)
  );

  useEffect(() => {
    if (!accountCompanies || variant === COMPONENT_VARIANTS.PROJECT) return;

    setLoading(true);
    setCompanies(
      accountCompanies.map((company) => {
        return { ...company, disabled: true };
      })
    );
    setLoading(false);
  }, [accountCompanies, variant]);

  useEffect(() => {
    if (
      !projectId ||
      variant === COMPONENT_VARIANTS.ACCOUNT ||
      enableAddCompanyDebounce === undefined ||
      (enableAddCompanyDebounce && debouncedCompanyInput === '')
    )
      return;

    doGetProjectCompanies(
      buildProjectCompaniesRequest(projectId, debouncedCompanyInput)
    );
  }, [
    debouncedCompanyInput,
    doGetProjectCompanies,
    enableAddCompanyDebounce,
    projectId,
    variant,
  ]);

  useEffect(() => {
    if (
      !accountCompanies ||
      !projectCompaniesData ||
      variant === COMPONENT_VARIANTS.ACCOUNT
    )
      return;

    setLoading(true);

    const { results: projectCompaniesResults } = projectCompaniesData;
    const projectCompanies = projectCompaniesResults.map((pc) => pc.id);

    setCompanies(
      accountCompanies.map((company) => {
        if (projectCompanies.includes(company.id))
          return {
            ...company,
            disabled: true,
          };
        return { ...company, disabled: false };
      })
    );
    setLoading(false);
  }, [accountCompanies, projectCompaniesData, variant]);

  return {
    companies,
    setCompanies,
    onChangeCompanyInput: enableAddCompanyDebounce
      ? debouncedInputChange.current
      : handleCompanyInputChange,
    isFetchingAccountCompanies,
    isFetchingProjectCompanies,
    loading,
  };
};

export default useGetCompanies;
